import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Chart from 'chart.js/auto';

interface KPIChartProps {
  data: any;
  type: 'line' | 'bar' | 'doughnut' | 'radar' | 'area';
  height?: number;
  options?: any;
}

const KPIChart: React.FC<KPIChartProps> = ({ 
  data, 
  type, 
  height = 300,
  options = {} 
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || !data) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Default chart configuration
    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            padding: 20,
            usePointStyle: true,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 8,
          titleFont: {
            size: 14,
            weight: 'bold'
          },
          bodyFont: {
            size: 13
          },
          callbacks: {
            label: function(context: any) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(context.parsed.y);
              }
              return label;
            }
          }
        }
      },
      scales: type !== 'doughnut' && type !== 'radar' ? {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 11
            },
            callback: function(value: any) {
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value);
            }
          }
        }
      } : undefined
    };

    // Merge default options with provided options
    const chartOptions = {
      ...defaultOptions,
      ...options,
      plugins: {
        ...defaultOptions.plugins,
        ...options.plugins
      }
    };

    // Prepare chart data with Wayne IA color scheme
    const chartData = {
      ...data,
      datasets: data.datasets?.map((dataset: any, index: number) => ({
        ...dataset,
        backgroundColor: dataset.backgroundColor || [
          'rgba(59, 130, 246, 0.8)',  // Blue
          'rgba(16, 185, 129, 0.8)',  // Green
          'rgba(139, 92, 246, 0.8)',  // Purple
          'rgba(251, 191, 36, 0.8)',  // Yellow
          'rgba(239, 68, 68, 0.8)',   // Red
          'rgba(107, 114, 128, 0.8)', // Gray
        ][index % 6],
        borderColor: dataset.borderColor || [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(139, 92, 246)',
          'rgb(251, 191, 36)',
          'rgb(239, 68, 68)',
          'rgb(107, 114, 128)',
        ][index % 6],
        borderWidth: dataset.borderWidth || 2,
        tension: type === 'line' ? 0.4 : undefined,
        fill: type === 'area' ? true : false
      }))
    };

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: type === 'area' ? 'line' : type,
      data: chartData,
      options: chartOptions
    });

    // Cleanup
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, type, options]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="relative"
      style={{ height: `${height}px` }}
    >
      <canvas ref={chartRef} />
    </motion.div>
  );
};

export default KPIChart;